import * as THREE from 'three';
import { io, Socket } from 'socket.io-client';
import { CityGenerator } from './CityGenerator';
import { PostmanController } from './PostmanController';
import { PackageManager } from './PackageManager';
import { LabelManager2D } from './LabelManager2D';
import { PortalManager } from './PortalManager';
import { PolicePatrolController } from './PolicePatrolController';
import { BillboardManager } from './BillboardManager';

// Mini Motorways style settings
const GAME_SETTINGS = {
  BG_COLOR: 0xE6F7FF,      // Light blue background (#E6F7FF)
  AMBIENT_LIGHT: 0xffffff, // White ambient light
  CAMERA_HEIGHT_DEFAULT: 15,
  CAMERA_MIN_HEIGHT: 5,
  CAMERA_MAX_HEIGHT: 50,
  CAMERA_ZOOM_SPEED: 3,
  TOPDOWN_VIEW_HEIGHT: 90  // Hauteur de la caméra en vue top-down
};

// Development flag - set to false before production build
const DEV_SKIP_WELCOME_OVERLAY = false;

class Game {
  private scene: THREE.Scene;
  private camera: THREE.PerspectiveCamera;
  private renderer: THREE.WebGLRenderer;
  private socket: Socket;
  private cityGenerator: CityGenerator;
  private postmanController: PostmanController;
  private packageManager: PackageManager;
  private labelManager: LabelManager2D;
  private portalManager: PortalManager | null = null;
  private policePatrolController: PolicePatrolController | null = null;
  private billboardManager: BillboardManager | null = null;
  private players: Map<string, THREE.Object3D> = new Map();
  private cameraHeight: number = GAME_SETTINGS.CAMERA_HEIGHT_DEFAULT;
  private minHeight: number = GAME_SETTINGS.CAMERA_MIN_HEIGHT;
  private maxHeight: number = GAME_SETTINGS.CAMERA_MAX_HEIGHT;
  private zoomSpeed: number = GAME_SETTINGS.CAMERA_ZOOM_SPEED;
  private isGridReceived: boolean = false;
  private targetCameraPosition: THREE.Vector3 = new THREE.Vector3();
  private currentCameraLookAt: THREE.Vector3 = new THREE.Vector3();
  private isTopDownView: boolean = false;
  private playerName: string = ''; // Store player name
  private gameStarted: boolean = false; // Track if game has started
  private lastTime: number = 0; // Added for collision detection

  constructor() {
    // Initialize Three.js scene
    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(GAME_SETTINGS.BG_COLOR);
    
    this.camera = new THREE.PerspectiveCamera(75, window.innerWidth / window.innerHeight, 0.1, 1000);
    
    // Set up renderer with Mini Motorways clean look
    this.renderer = new THREE.WebGLRenderer({ 
      antialias: true,
      alpha: true 
    });
    this.renderer.setSize(window.innerWidth, window.innerHeight);
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    document.body.appendChild(this.renderer.domElement);

    // Add camera to the scene with a name for raycasting later
    this.camera.position.set(0, this.cameraHeight, 8); // Start slightly behind origin
    this.camera.lookAt(0, 2, 0); // Look at point slightly above ground
    this.camera.name = 'camera';
    this.scene.add(this.camera);
    
    // Initialize label manager (now using 2D version)
    this.labelManager = new LabelManager2D(this.camera);
    
    // Initialize camera targets with current position
    this.targetCameraPosition.copy(this.camera.position);
    this.currentCameraLookAt.set(0, 2, 0);
    
    // Setup mouse wheel zoom
    this.setupZoom();

    // Add softer lighting for Mini Motorways style
    this.setupLighting();

    // Initialize socket connection - Utiliser HTTPS à la place de HTTP
    const serverUrl = window.location.protocol === 'https:' 
      ? `https://${window.location.hostname}` 
      : `http://${window.location.hostname}:3001`;
    
    // Configuration Socket.IO avec options explicites pour forcer HTTPS
    const socketOptions = {
      path: '/socket.io/',
      transports: ['websocket', 'polling'],
      secure: true,
      rejectUnauthorized: false,
      forceNew: true,
      // Force l'utilisation du même protocole que la page
      // Pour éviter les requêtes en HTTP quand on est en HTTPS
      rememberUpgrade: true
    };
    
    // En HTTPS, utiliser le même protocole et domaine sans spécifier de port
    // En HTTP, utiliser l'URL complète avec le port
    console.log("Protocol:", window.location.protocol);
    console.log("Connecting to:", serverUrl);
    this.socket = io(window.location.protocol === 'https:' ? window.location.origin : serverUrl, socketOptions);
    
    // Stocker l'ID du joueur dans le localStorage pour permettre au LabelManager2D de l'identifier
    this.socket.on('connect', () => {
      if (this.socket.id) {
        window.localStorage.setItem('playerId', this.socket.id);
        console.log('Player connected with ID:', this.socket.id);
      }
    });
    
    this.setupSocketListeners();

    // Initialize game components but don't generate city until grid is received
    this.cityGenerator = new CityGenerator(this.scene);
    this.postmanController = new PostmanController(this.scene, this.socket, this.cityGenerator);
    this.packageManager = new PackageManager(this.scene, this.socket, this.postmanController, this.camera);
    this.policePatrolController = new PolicePatrolController(this.scene, this.socket, this.cityGenerator);

    // Pass the label manager to the postman controller
    this.postmanController.setLabelManager(this.labelManager);
    
    // Pass the label manager to the police patrol controller
    if (this.policePatrolController) {
      this.policePatrolController.setLabelManager(this.labelManager);
    }

    // Connecter le contrôleur au changement de vue de la caméra
    this.postmanController.setOnCameraViewChanged(this.handleCameraViewChanged.bind(this));
    
    // Setup welcome overlay handlers
    this.setupWelcomeOverlay();

    // Start animation loop
    this.animate();

    // Handle window resize
    window.addEventListener('resize', this.onWindowResize.bind(this));
  }

  private setupLighting(): void {
    // Clear existing lights
    this.scene.children
      .filter(child => child.type.includes('Light'))
      .forEach(light => this.scene.remove(light));
    
    // Add soft ambient light (increased from 0.7 to 0.9)
    const ambientLight = new THREE.AmbientLight(GAME_SETTINGS.AMBIENT_LIGHT, 0.9);
    this.scene.add(ambientLight);

    // Add sun-like directional light from top left corner (increased from 1.0 to 1.3)
    const sunLight = new THREE.DirectionalLight(0xfffbe6, 1.3);
    sunLight.position.set(-100, 150, -100); // Top left position
    sunLight.castShadow = true;
    
    // Configure shadow properties for the sun light
    sunLight.shadow.mapSize.width = 2048;
    sunLight.shadow.mapSize.height = 2048;
    sunLight.shadow.camera.near = 0.5;
    sunLight.shadow.camera.far = 500;
    sunLight.shadow.camera.left = -100;
    sunLight.shadow.camera.right = 100;
    sunLight.shadow.camera.top = 100;
    sunLight.shadow.camera.bottom = -100;
    sunLight.shadow.radius = 2;
    sunLight.shadow.bias = -0.0005;
    
    this.scene.add(sunLight);

    // Add directional light with soft shadows (increased from 0.3 to 0.5)
    const directionalLight = new THREE.DirectionalLight(0xffffff, 0.5);
    directionalLight.position.set(50, 100, 50);
    directionalLight.castShadow = true;
    
    // Configure shadow properties for softer Mini Motorways look
    directionalLight.shadow.mapSize.width = 2048;
    directionalLight.shadow.mapSize.height = 2048;
    directionalLight.shadow.camera.near = 0.5;
    directionalLight.shadow.camera.far = 500;
    directionalLight.shadow.camera.left = -100;
    directionalLight.shadow.camera.right = 100;
    directionalLight.shadow.camera.top = 100;
    directionalLight.shadow.camera.bottom = -100;
    directionalLight.shadow.radius = 4;
    directionalLight.shadow.bias = -0.0005;
    
    this.scene.add(directionalLight);
    
    // Add a subtle hemisphere light for better color grading (increased from 0.3 to 0.5)
    const hemisphereLight = new THREE.HemisphereLight(0x91d9e8, 0xffffff, 0.7);
    this.scene.add(hemisphereLight);
  }

  private setupSocketListeners(): void {
    this.socket.on('playerJoined', (player) => {
      this.addPlayer(player);
    
    });

    this.socket.on('playerLeft', (playerId) => {
      this.removePlayer(playerId);
      
      // Remove home marker when player leaves
      this.cityGenerator.removeHomeMarker(playerId);
    });

    // Handle player disconnection
    this.socket.on('playerDisconnected', (playerId) => {
      this.removePlayer(playerId);
      
      // Remove home marker when player disconnects
      this.cityGenerator.removeHomeMarker(playerId);
    });

    this.socket.on('playerMoved', (data) => {
      this.updatePlayerPosition(data);
    });
    
    // Listen for player position updates from server
    this.socket.on('playerPositionUpdate', (data) => {
      this.updatePlayerPosition(data);
    });
    
    // Handle player updates (like name changes)
    this.socket.on('playerUpdated', (data) => {
      const player = this.players.get(data.id);
      if (player) {
        // Store player name in userData for display purposes
        player.userData.name = data.name;
        console.log(`Updated player ${data.id} with name: ${data.name}`);
        
        // Update the player's name label
        this.labelManager.updateName(data.id, data.name, player.position);
      } else {
        console.warn(`Received playerUpdated for unknown player ID: ${data.id}, name: ${data.name}`);
      }
    });

    this.socket.on('existingPlayers', (players) => {
      console.log(`Received ${players.length} existing players from server`);
      players.forEach((player: any) => {
        console.log(`Processing existing player: ${player.id}`, player.name ? `with name '${player.name}'` : `with no name`);
        this.addPlayer(player);
        
        
      });
    });

    // Add handler for receiving the city grid from the server
    this.socket.on('cityGrid', (cityGrid) => {
      console.log('Received city grid from server');
      this.cityGenerator.setGrid(cityGrid);
      
      // Now that we have the grid, we can generate the city
      this.cityGenerator.generate();
      this.isGridReceived = true;
      
      // If we have our own player info with home position, render our own home marker
      if (this.socket.id) {
        // Request our initial position from the server
        this.socket.emit('requestInitialPosition');

      }
    });
  }

  private addPlayer(player: any): void {
    if (player.id === this.socket.id) return;

    console.log('Adding player', player);

    // Create player vehicle using FBX model
    const vehicle = new THREE.Group();
    
    // Store the player's name if available
    if (player.name) {
      vehicle.userData.name = player.name;
    }
    
    // Utiliser le même modèle FBX que pour le joueur local
    const assetPath = 'cartooncity_141_fbx/Cars/pickupwheels1.fbx';
    const position = new THREE.Vector3(0, -0.27, 0); // Position verticale ajustée de -0.4 à -0.27 (réduction de 33%)
    const rotation = new THREE.Euler(-Math.PI/2, 0, 0); // Rotation autour de l'axe Y de 90 degrés
    const scale = new THREE.Vector3(0.00335, 0.00335, 0.00335); // Échelle réduite de 33% (de 0.005 à 0.00335)
    
    // Utiliser la méthode loadModelAsset du CityGenerator pour charger le modèle FBX
    this.cityGenerator.loadModelAsset(assetPath, position, rotation, scale, vehicle, null);
    
    // Positionner le véhicule
    if (player.position) {
      if (player.position instanceof THREE.Vector3) {
        vehicle.position.copy(player.position);
      } else {
        vehicle.position.set(
          player.position.x || 0,
          player.position.y || 0.27,
          player.position.z || 0
        );
      }
    }
    
    // Apply initial rotation if provided
    if (player.rotation !== undefined) {
      vehicle.rotation.y = player.rotation;
    }
    
    this.scene.add(vehicle);
    this.players.set(player.id, vehicle);
    
    // Create a name label for the player if they have a name
    if (player.name) {
      console.log(`Creating name label for player ${player.id}: ${player.name}`);
      this.labelManager.createLabel(player.id, player.name, vehicle.position);
    } else {
      console.log(`Player ${player.id} has no name, skipping label creation`);
    }
  }

  private removePlayer(playerId: string): void {
    const playerObject = this.players.get(playerId);
    if (playerObject) {
      this.scene.remove(playerObject);
      this.players.delete(playerId);
      
      // Remove the player's name label
      this.labelManager.removeLabel(playerId);
    }
  }

  private updatePlayerPosition(data: any): void {
    const playerObject = this.players.get(data.id);
    if (playerObject) {
      // Vérifier si data.position est un objet Vector3 ou un objet simple avec x, y, z
      if (data.position) {
        if (data.position instanceof THREE.Vector3) {
          playerObject.position.copy(data.position);
        } else {
          playerObject.position.set(data.position.x, data.position.y || 0.27, data.position.z);
        }
        
        // Update the player's name label position
        this.labelManager.updateLabel(data.id, playerObject.position);
      }
      
      // Apply rotation if provided
      if (data.rotation !== undefined) {
        playerObject.rotation.y = data.rotation;
      }
      
      // Apply speed if provided
      if (data.speed !== undefined) {
        // Mettre à jour la vitesse du joueur dans le groupe
        playerObject.userData.speed = data.speed;
      }
      
      // Keep the direction-based rotation as a fallback
      else if (data.direction && (data.direction instanceof THREE.Vector3 ? data.direction.length() > 0 : true)) {
        const direction = data.direction instanceof THREE.Vector3 ? 
          data.direction : 
          new THREE.Vector3(data.direction.x, data.direction.y, data.direction.z);
        
        if (direction.length() > 0) {
          const angle = Math.atan2(direction.x, direction.z);
          playerObject.rotation.y = angle;
        }
      }
    }
  }

  private onWindowResize(): void {
    this.camera.aspect = window.innerWidth / window.innerHeight;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(window.innerWidth, window.innerHeight);
    
    // Update all player labels as screen positions will have changed
    if (this.players) {
      this.players.forEach((player, playerId) => {
        this.labelManager.updateLabel(playerId, player.position);
      });
    }
    
    // Update own player label
    if (this.postmanController && this.socket.id) {
      const playerPosition = this.postmanController.getPosition();
      this.labelManager.updateLabel(this.socket.id, playerPosition);
    }
  }

  private setupZoom(): void {
    // Add event listener for mouse wheel to zoom in/out
    window.addEventListener('wheel', (event) => {
      // Adjust camera height based on wheel direction
      this.cameraHeight += event.deltaY * 0.01 * this.zoomSpeed;
      
      // Clamp to min/max heights - use lower values for third-person view
      this.minHeight = 5;  // Closer for third-person view
      this.maxHeight = 50; // Farther for wider view
      this.cameraHeight = Math.max(this.minHeight, Math.min(this.maxHeight, this.cameraHeight));
    });
  }

  private handleCameraViewChanged(isTopDown: boolean): void {
    this.isTopDownView = isTopDown;
  }

  private setupWelcomeOverlay(): void {
    const welcomeOverlay = document.getElementById('welcome-overlay');
    const playButton = document.getElementById('play-button');
    const nameInput = document.getElementById('name-input') as HTMLInputElement;
    
    if (!welcomeOverlay || !playButton || !nameInput) {
      console.error('Welcome overlay elements not found');
      return;
    }
    
    // Skip welcome overlay in development mode
    if (DEV_SKIP_WELCOME_OVERLAY) {
      console.log('Development mode: Skipping welcome overlay');
      welcomeOverlay.style.display = 'none';
      this.playerName = 'Dev Player';
      // Start the game immediately
      this.startGame();
      return;
    }
    
    // Handle play button click
    const startGameHandler = () => {
      this.playerName = nameInput.value.trim();
      // TODO: Make name mandatory in the future
      
      // Hide the welcome overlay
      welcomeOverlay.style.opacity = '0';
      welcomeOverlay.style.transition = 'opacity 0.5s';
      
      setTimeout(() => {
        if (welcomeOverlay) {
          welcomeOverlay.style.display = 'none';
        }
        // Start the game
        this.startGame();
      }, 500);
    };
    
    // Add click event listener
    playButton.addEventListener('click', startGameHandler);
    
    // Add Enter key event listener
    nameInput.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        startGameHandler();
      }
    });
  }
  
  // Start the game after player clicks "Play"
  private startGame(): void {
    this.gameStarted = true;
    console.log("Starting game with player name:", this.playerName);
    
    // Hide welcome overlay
    const welcomeOverlay = document.getElementById('welcome-overlay');
    if (welcomeOverlay) {
      welcomeOverlay.style.display = 'none';
    }
    
    // Wait for grid to be received, then notify
    if (!this.isGridReceived) {
      console.log("Waiting for city grid from server...");
      // Request a new grid if we don't have one yet
      this.socket.emit('requestGrid');
      
      // Set up listener to handle grid when it arrives
      const waitForGrid = () => {
        if (this.isGridReceived) {
          this.postmanController.enableControls();
          console.log("Grid received, game ready!");
          
          // Initialize billboard manager after grid is received
          this.initializeBillboards();
        } else {
          setTimeout(waitForGrid, 500); // Check again in 500ms
        }
      };
      waitForGrid();
    } else {
      // Grid already received, continue immediately
      this.postmanController.enableControls();
      
      // Initialize billboard manager
      this.initializeBillboards();
    }
    
    // Set player info to server
    if (this.playerName) {
      console.log(`Sending player name to server: ${this.playerName}`);
      this.socket.emit('setPlayerName', { name: this.playerName });
      
      // Add the name label to our own player
      const playerPosition = this.postmanController.getPosition();
      if (this.socket.id) {
        console.log(`Creating name label for own player (${this.socket.id}): ${this.playerName}`);
        this.labelManager.createLabel(this.socket.id, this.playerName, playerPosition);
      }
    }
    
    // Enable controls in the PostmanController
    if (this.postmanController) {
      this.postmanController.enableControls();
    }
    
    // Initialize portal manager after game has started
    this.portalManager = new PortalManager(this.scene, this.postmanController, this.playerName);
    
    // Check if we have an exit portal and connect it to the portal manager with a small delay
    // to ensure everything is loaded properly
    setTimeout(() => {
      if (this.cityGenerator.getExitPortalBox() && this.portalManager) {
        console.log("Connecting exit portal box to portal manager");
        this.portalManager.setExitPortalBox(this.cityGenerator.getExitPortalBox()!);
      } else {
        console.warn("Exit portal box not found");
      }

      if (this.cityGenerator.getExitPortalGroup() && this.portalManager) {
        console.log("Connecting exit portal group to portal manager");
        this.portalManager.setExitPortalGroup(this.cityGenerator.getExitPortalGroup()!);
      } else {
        console.warn("Exit portal group not found");
      }
    }, 1000);
    
    console.log(`Game started with player name: ${this.playerName || 'Anonymous'}`);
  }

  // Initialize billboards in the city
  private initializeBillboards(): void {
    console.log("Initializing billboards in the city");
    
    // Create billboard manager
    this.billboardManager = new BillboardManager(this.scene, this.camera);
    
    // Place billboards in the city
    this.billboardManager.placeBillboardsInCity(
      this.cityGenerator.getCityGrid().citySize || 100,
      this.cityGenerator.getBlockSize(),
      this.cityGenerator.getRoadWidth()
    );
  }

  private animate(): void {
    requestAnimationFrame(this.animate.bind(this));
    
    // Only update when the game has started
    if (!this.gameStarted) return;

    // Get the current time
    const time = performance.now();
    
    // Calculate delta time in seconds
    const delta = (time - this.lastTime) / 1000;
    
    // Update last time
    this.lastTime = time;
    
    // Check if grid has been received from server
    if (!this.isGridReceived) return;
    
    // Update postman controller
    if (this.postmanController) {
      this.postmanController.update();
      
      // Check for player collisions
      this.checkPlayerCollisions();
    }
    
    // Update package manager to check for collisions and pickup
    if (this.packageManager) this.packageManager.update();
    
    // Update portal manager for collisions - ensure it runs every frame
    if (this.portalManager) this.portalManager.update();
    
    // Mettre à jour le contrôleur de patrouilles de police
    if (this.policePatrolController) {
      this.policePatrolController.update();
    }
    
    // Update smooth camera transitions
    this.updateCamera();
    
    // Render
    this.renderer.render(this.scene, this.camera);
  }
  
  // Nouvelle méthode pour vérifier les collisions entre joueurs
  private checkPlayerCollisions(): void {
    if (!this.postmanController) return;
    
    const playerPosition = this.postmanController.getPosition();
    const playerObject = this.postmanController.getPlayer();
    
    // Rayon de collision pour les joueurs
    const collisionRadius = 2.0;
    
    // Vérifier les collisions avec les autres joueurs
    this.players.forEach((otherPlayer, otherPlayerId) => {
      // Ne pas vérifier la collision avec soi-même
      if (otherPlayerId === this.socket.id) return;
      
      // Calculer la distance entre les joueurs
      const distance = playerPosition.distanceTo(otherPlayer.position);
      
      // Si la distance est inférieure au seuil, il y a collision
      if (distance < collisionRadius) {
        console.log(`Collision detected with player ${otherPlayerId}`);
        
        // Informer le serveur de la collision
        this.socket.emit('playerCollision', { otherPlayerId });
      }
    });
  }

  private updateCamera(): void {
    // Update camera position to follow player
    const playerPosition = this.postmanController.getPosition();
    
    // La mise à jour de la caméra dépend du mode de vue
    if (this.isTopDownView) {
      // Vue top-down
      const topDownHeight = GAME_SETTINGS.TOPDOWN_VIEW_HEIGHT;
      this.targetCameraPosition.set(playerPosition.x, topDownHeight, playerPosition.z);
      this.currentCameraLookAt.copy(playerPosition);
    } else {
      // Vue à la 3ème personne normale
      // Calculate camera position for third-person view
      // Position camera behind the player based on player's rotation
      const player3D = this.postmanController.getPlayer();
      const rotationY = player3D.rotation.y;
      
      // Calculate offset based on player rotation (position camera behind player)
      const cameraOffset = new THREE.Vector3(
        Math.sin(rotationY) * -5, // X offset (reduced from -8 to -5 to be closer)
        2 + this.cameraHeight * 0.1,   // Y offset (reduced from 3 to 2 and from 0.15 to 0.1 to be lower)
        Math.cos(rotationY) * -5  // Z offset (reduced from -8 to -5 to be closer)
      );
      
      // Set target camera position for smooth interpolation
      this.targetCameraPosition.copy(playerPosition).add(cameraOffset);
      
      // Target to look at (slightly in front of player)
      this.currentCameraLookAt.copy(playerPosition).add(
        new THREE.Vector3(
          Math.sin(rotationY) * 1.5,
          0.3, // Look slightly lower (changed from 0.5 to 0.3)
          Math.cos(rotationY) * 1.5
        )
      );
    }
    
    // Smooth camera movement
    this.camera.position.lerp(this.targetCameraPosition, 0.12);
    
    // Look at player
    this.camera.lookAt(this.currentCameraLookAt);
    
    // Update player name labels
    this.labelManager.update();
    
    // Update portal labels to face the camera
    this.cityGenerator.updatePortalLabels(this.camera);
  }
  
  /**
   * Clean up resources when game is destroyed
   */
  public cleanup(): void {
    console.log("Cleaning up game resources");
    
    // Remove event listeners
    window.removeEventListener('resize', this.onWindowResize.bind(this));
    window.removeEventListener('wheel', this.setupZoom.bind(this));
    
    // Clean up billboard manager
    if (this.billboardManager) {
      this.billboardManager.cleanup();
      this.billboardManager = null;
    }
    
    // Clean up portal manager
    if (this.portalManager) {
      this.portalManager = null;
    }
    
    // Clean up players
    this.players.forEach((player, id) => {
      this.removePlayer(id);
    });
    
    // Clean up city generator
    this.cityGenerator.cleanup();
    
    // Clean up renderer
    if (this.renderer && this.renderer.domElement && this.renderer.domElement.parentNode) {
      this.renderer.domElement.parentNode.removeChild(this.renderer.domElement);
    }
    
    // Disconnect socket
    if (this.socket) {
      this.socket.disconnect();
    }
    
    console.log("Game resources cleaned up");
  }
}

// Start the game
const game = new Game();

// Add event listener for beforeunload to clean up resources
window.addEventListener('beforeunload', () => {
  game.cleanup();
}); 